import React from 'react';
import { Plus, X } from 'lucide-react';
import { PAGE_W, PAGE_PAD_X } from '../lib/dossierThemes';

// ───────────────────────────────────────────────────────────────────────────
// DossierContentEditor — structured, inline-editable render of the swiss_grid
// content pages (Formation + Langues, Compétences, Expériences). Edits the
// dossier's structured fields directly; mirrors backend/templates/swiss_grid.html
// so the editor preview matches the exported PDF.
// ───────────────────────────────────────────────────────────────────────────

const MONO = "'SF Mono','Liberation Mono','Courier New',monospace";

const baseInput = {
  background: 'transparent', border: 'none', outline: 'none', padding: '1px 3px',
  margin: 0, fontFamily: 'inherit', borderRadius: 3, width: '100%',
};

// Small inline text input that looks like the surrounding text until hovered.
const Field = ({ value, onChange, placeholder, style, mono, theme, multiline, rows = 1 }) => {
  const common = {
    ...baseInput,
    ...(mono ? { fontFamily: MONO } : {}),
    ...style,
  };
  const cls = 'dce-input';
  if (multiline) {
    return (
      <textarea
        className={cls}
        value={value}
        placeholder={placeholder}
        rows={rows}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...common, resize: 'none', overflow: 'hidden', lineHeight: 1.5 }}
        onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
      />
    );
  }
  return (
    <input
      className={cls}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      style={common}
    />
  );
};

const RemoveBtn = ({ onClick, title = 'Supprimer' }) => (
  <button type="button" onClick={onClick} title={title} className="dce-remove"
    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, borderRadius: 5, border: '1px solid #fecaca', background: '#fff', color: '#ef4444', cursor: 'pointer', flexShrink: 0 }}>
    <X size={12} />
  </button>
);

const AddBtn = ({ onClick, label }) => (
  <button type="button" onClick={onClick}
    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10, padding: '6px 12px', borderRadius: 7, border: '1px dashed #cbd5e1', background: '#f8fafc', color: '#475569', fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }}>
    <Plus size={13} /> {label}
  </button>
);

const SectionHead = ({ num, title, theme }) => (
  <>
    <div style={{ height: 2, background: theme.line, marginBottom: 14 }} />
    <div style={{ marginBottom: 18 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: theme.accent, marginRight: 10, fontFamily: MONO }}>{num}</span>
      <span style={{ fontSize: 19, fontWeight: 700, letterSpacing: -0.4, color: theme.ink }}>{title}</span>
    </div>
  </>
);

const DossierContentEditor = ({
  theme,
  formations, setFormations,
  languages, setLanguages,
  competences, setCompetences,
  experiences, setExperiences,
}) => {
  // ── mutators ──
  const updateAt = (list, setList, i, patch) => setList(list.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const removeAt = (list, setList, i) => setList(list.filter((_, idx) => idx !== i));

  return (
    <div style={{ width: PAGE_W, padding: `30px ${PAGE_PAD_X}px`, fontFamily: "'Liberation Sans', Arial, Helvetica, sans-serif", color: theme.text }}>
      <style>{`
        .dce-input:hover { background: rgba(37,99,235,0.06); box-shadow: 0 0 0 1px rgba(37,99,235,0.16); }
        .dce-input:focus { background: rgba(37,99,235,0.09); box-shadow: 0 0 0 1.5px rgba(37,99,235,0.4); }
        .dce-input::placeholder { color: #b6c2d4; }
        .dce-row { position: relative; }
        .dce-row > .dce-rowtools { opacity: 0; transition: opacity .12s; }
        .dce-row:hover > .dce-rowtools { opacity: 1; }
      `}</style>

      {/* ═══ 01 FORMATION ═══ */}
      <div style={{ paddingTop: 14, marginBottom: 30 }}>
        <SectionHead num="01" title="Formation" theme={theme} />

        {formations.map((f, i) => (
          <div key={i} className="dce-row" style={{ display: 'flex', gap: 14, padding: '11px 0', borderBottom: `1px solid ${theme.hair}`, alignItems: 'flex-start' }}>
            <div style={{ width: 130, flexShrink: 0, paddingTop: 1 }}>
              <Field value={f.year} onChange={(v) => updateAt(formations, setFormations, i, { year: v })} placeholder="2019 — 2021" mono style={{ fontSize: 10.5, fontWeight: 700, color: theme.muted }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                <Field value={f.title} onChange={(v) => updateAt(formations, setFormations, i, { title: v })} placeholder="Intitulé du diplôme" style={{ fontSize: 12, fontWeight: 700, color: theme.ink, flex: 1 }} />
                <span style={{ color: theme.muted, fontSize: 10.5 }}>·</span>
                <Field value={f.level} onChange={(v) => updateAt(formations, setFormations, i, { level: v })} placeholder="niveau (ex: Titre niveau I)" style={{ fontSize: 10.5, fontWeight: 500, color: theme.muted, width: 150 }} />
              </div>
              <Field value={f.institution} onChange={(v) => updateAt(formations, setFormations, i, { institution: v })} placeholder="Établissement" style={{ fontSize: 10.5, color: theme.muted, marginTop: 2 }} />
            </div>
            <div className="dce-rowtools" style={{ paddingTop: 2 }}>
              <RemoveBtn onClick={() => removeAt(formations, setFormations, i)} />
            </div>
          </div>
        ))}
        <AddBtn label="Ajouter une formation" onClick={() => setFormations([...formations, { year: '', title: '', level: '', institution: '' }])} />

        {/* Langues & certifications */}
        <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: theme.faint, margin: '24px 0 12px' }}>
          Langues &amp; certifications
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 24px' }}>
          {languages.map((l, i) => (
            <div key={i} className="dce-row" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <div style={{ flexShrink: 0 }}>
                <input className="dce-input" value={l.level} placeholder="C1" onChange={(e) => updateAt(languages, setLanguages, i, { level: e.target.value })}
                  style={{ ...baseInput, width: 42, textAlign: 'center', padding: '6px 4px', borderRadius: 6, background: theme.badgeBg, color: theme.badgeText, fontSize: 12, fontWeight: 800 }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                  <Field value={l.language} onChange={(v) => updateAt(languages, setLanguages, i, { language: v })} placeholder="Anglais" style={{ fontSize: 11.5, fontWeight: 700, color: theme.ink, width: 80 }} />
                  <span style={{ color: theme.muted, fontSize: 11 }}>—</span>
                  <Field value={l.certification} onChange={(v) => updateAt(languages, setLanguages, i, { certification: v })} placeholder="TOEIC" style={{ fontSize: 11.5, fontWeight: 700, color: theme.ink, flex: 1 }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                  <Field value={l.institution} onChange={(v) => updateAt(languages, setLanguages, i, { institution: v })} placeholder="Organisme" style={{ fontSize: 10, color: theme.muted, flex: 1 }} />
                  <span style={{ color: theme.muted, fontSize: 10 }}>·</span>
                  <Field value={l.year} onChange={(v) => updateAt(languages, setLanguages, i, { year: v })} placeholder="2022—2023" mono style={{ fontSize: 10, color: theme.muted, width: 80 }} />
                </div>
              </div>
              <div className="dce-rowtools"><RemoveBtn onClick={() => removeAt(languages, setLanguages, i)} /></div>
            </div>
          ))}
        </div>
        <AddBtn label="Ajouter une langue" onClick={() => setLanguages([...languages, { language: '', level: '', certification: '', institution: '', year: '' }])} />
      </div>

      {/* ═══ 02 COMPÉTENCES ═══ */}
      <div style={{ paddingTop: 14, marginBottom: 30 }}>
        <SectionHead num="02" title="Compétences" theme={theme} />

        {competences.map((g, gi) => (
          <div key={gi} className="dce-row" style={{ display: 'flex', gap: 18, padding: '12px 0', borderBottom: `1px solid ${theme.hair}`, alignItems: 'flex-start' }}>
            <div style={{ width: 180, flexShrink: 0, paddingTop: 3 }}>
              <Field value={g.domain} onChange={(v) => setCompetences(competences.map((x, i) => i === gi ? { ...x, domain: v } : x))} placeholder="Catégorie" style={{ fontSize: 11.5, fontWeight: 700, color: theme.ink }} />
            </div>
            <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 7, alignItems: 'center' }}>
              {g.tags.map((tag, ti) => (
                <span key={ti} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 8px 5px 11px', background: theme.chipBg, color: theme.chipText, borderRadius: 14, fontSize: 10, fontWeight: 600 }}>
                  {tag}
                  <button type="button" title="Retirer" onClick={() => {
                    const tags = g.tags.filter((_, i) => i !== ti);
                    setCompetences(competences.map((x, i) => i === gi ? { ...x, tags } : x));
                  }} style={{ border: 'none', background: 'transparent', color: theme.chipText, cursor: 'pointer', padding: 0, display: 'inline-flex', opacity: 0.6 }}><X size={11} /></button>
                </span>
              ))}
              <input
                className="dce-input"
                placeholder="+ tag (Entrée)"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.target.value.trim()) {
                    e.preventDefault();
                    const tags = [...g.tags, e.target.value.trim()];
                    setCompetences(competences.map((x, i) => i === gi ? { ...x, tags } : x));
                    e.target.value = '';
                  }
                }}
                style={{ ...baseInput, width: 110, fontSize: 10, padding: '4px 8px', borderRadius: 14, border: '1px dashed #cbd5e1' }}
              />
            </div>
            <div className="dce-rowtools" style={{ paddingTop: 3 }}><RemoveBtn onClick={() => removeAt(competences, setCompetences, gi)} title="Supprimer la catégorie" /></div>
          </div>
        ))}
        <AddBtn label="Ajouter une catégorie" onClick={() => setCompetences([...competences, { domain: '', tags: [] }])} />
      </div>

      {/* ═══ 03 EXPÉRIENCES ═══ */}
      <div style={{ paddingTop: 14, marginBottom: 30 }}>
        <SectionHead num="03" title="Expériences professionnelles" theme={theme} />

        {experiences.map((e, i) => (
          <div key={i} className="dce-row" style={{ display: 'flex', gap: 16, paddingBottom: 22, marginBottom: 4 }}>
            <div style={{ width: 150, flexShrink: 0 }}>
              <input className="dce-input" value={e.duree} placeholder="Oct. 2025 — Présent" onChange={(ev) => updateAt(experiences, setExperiences, i, { duree: ev.target.value })}
                style={{ ...baseInput, fontFamily: MONO, padding: '4px 9px', borderRadius: 5, background: theme.dateBg, color: theme.accent, fontSize: 9.5, fontWeight: 700 }} />
              <Field value={e.entreprise} onChange={(v) => updateAt(experiences, setExperiences, i, { entreprise: v })} placeholder="Entreprise" style={{ fontSize: 11.5, fontWeight: 700, color: theme.ink, marginTop: 9 }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 9 }}>
                <Field value={e.poste} onChange={(v) => updateAt(experiences, setExperiences, i, { poste: v })} placeholder="Intitulé du poste" style={{ fontSize: 14, fontWeight: 700, color: theme.ink, flex: 1 }} />
                <span style={{ color: theme.accent, fontSize: 13, fontWeight: 700 }}>—</span>
                <Field value={e.seniority} onChange={(v) => updateAt(experiences, setExperiences, i, { seniority: v })} placeholder="séniorité" style={{ fontSize: 13, fontWeight: 700, color: theme.accent, width: 90 }} />
              </div>
              <Field
                value={e.mission}
                onChange={(v) => updateAt(experiences, setExperiences, i, { mission: v })}
                placeholder={'Une mission/réalisation par ligne.\nMettez en valeur un chiffre avec **gras**.'}
                multiline
                rows={Math.max(3, (e.mission || '').split('\n').length)}
                style={{ fontSize: 10.5, color: theme.text, width: '100%' }}
              />
              <Field value={e.environnement} onChange={(v) => updateAt(experiences, setExperiences, i, { environnement: v })} placeholder="Environnement technique (optionnel)" style={{ fontSize: 9.5, color: theme.muted, marginTop: 6 }} />
            </div>
            <div className="dce-rowtools"><RemoveBtn onClick={() => removeAt(experiences, setExperiences, i)} /></div>
          </div>
        ))}
        <AddBtn label="Ajouter une expérience" onClick={() => setExperiences([...experiences, { duree: '', poste: '', seniority: '', entreprise: '', mission: '', environnement: '' }])} />
      </div>
    </div>
  );
};

export default DossierContentEditor;
